import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import { productsApi } from '@/lib/api/arrex/catalog';
import { settingsApi } from '@/lib/api/arrex/settings';
import { useApp } from '@/contexts/AppContext';
import { localized, imgUrl } from '@/lib/format';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { ArrowUp, ArrowDown, Trash2, ChevronDown, Check } from 'lucide-react';
import { cn } from '@/lib/utils';
import { ListSkeleton } from '@/components/ui/skeletons';
import { Loader2 } from 'lucide-react';
import { useState as useStateHook } from 'react';

async function fetchAllProducts(): Promise<any[]> {
  const all: any[] = [];
  let page = 1;
  // eslint-disable-next-line no-constant-condition
  while (true) {
    const { items, totalPages } = await productsApi.adminList({ page, pageSize: 60, sort: 'newest' });
    all.push(...(items || []));
    if (page >= (totalPages || 1)) break;
    page++;
  }
  return all;
}


function ProductPicker({
  products, value, onChange, lang,
}: { products: any[]; value: string; onChange: (v: string) => void; lang: string }) {
  const [open, setOpen] = useStateHook(false);
  const { t } = useTranslation();
  const current = products.find(p => p.id === value);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="w-full flex items-center justify-between gap-2 h-12 px-4 rounded-lg border border-border bg-background/80 hover:border-primary/50 transition-all text-start"
        >
          <span className="flex items-center gap-3 min-w-0">
            {current?.main_image_url ? (
              <img src={imgUrl(current.main_image_url)} className="w-7 h-7 rounded object-cover shrink-0" />
            ) : (
              <span className="text-[10px] font-mono uppercase text-muted-foreground bg-muted/60 px-1.5 py-0.5 rounded">PRD</span>
            )}
            <span className="text-sm font-medium truncate">
              {current ? localized(current, 'name', lang as any) : t('admin.colorsPage.chooseProduct')}
            </span>
          </span>
          <ChevronDown className={cn('w-4 h-4 text-muted-foreground transition-transform shrink-0', open && 'rotate-180')} />
        </button>
      </PopoverTrigger>
      <PopoverContent className="p-2 w-[--radix-popover-trigger-width] max-h-80 overflow-y-auto rounded-lg" align="start">
        <div className="space-y-1">
          {products.map(p => {
            const selected = p.id === value;
            return (
              <button key={p.id} type="button"
                onClick={() => { onChange(p.id); setOpen(false); }}
                className={cn(
                  'w-full flex items-center gap-3 px-3 py-2 rounded-md text-start transition-colors',
                  selected ? 'bg-primary/15' : 'hover:bg-muted/60'
                )}
              >
                {p.main_image_url && <img src={imgUrl(p.main_image_url)} className="w-7 h-7 rounded object-cover shrink-0" />}
                <span className="text-sm truncate flex-1">{localized(p, 'name', lang as any)}</span>
                {selected && <Check className="w-4 h-4 text-primary shrink-0" />}
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}

export default function AdminBuyItWith() {
  const { t } = useTranslation();
  const { lang } = useApp();
  const [products, setProducts] = useState<any[]>([]);
  const [mainId, setMainId] = useState<string>('');
  const [related, setRelated] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingRelated, setLoadingRelated] = useState(false);

  useEffect(() => {
    fetchAllProducts()
      .then(setProducts)
      .catch(() => setProducts([]))
      .finally(() => setLoading(false));
  }, []);

  const loadRelated = async (pid: string) => {
    setLoadingRelated(true);
    try {
      const { items } = await settingsApi.adminBuyItWith(pid);
      setRelated(items || []);
    } finally {
      setLoadingRelated(false);
    }
  };

  useEffect(() => { if (mainId) loadRelated(mainId); else setRelated([]); }, [mainId]);

  const isRelated = (id: string) => related.some(r => r.related_product_id === id);

  const toggle = async (id: string) => {
    if (!mainId || id === mainId) return;
    if (isRelated(id)) {
      try { await settingsApi.removeBuyItWith(mainId, id); } catch (e: any) { return toast.error(e?.message || 'Failed'); }
    } else {
      try { await settingsApi.addBuyItWith(mainId, id); } catch (e: any) { return toast.error(e?.message || 'Failed'); }
    }
    loadRelated(mainId);
  };

  const move = async (idx: number, dir: -1 | 1) => {
    const next = idx + dir;
    if (next < 0 || next >= related.length) return;
    const a = related[idx], b = related[next];
    await settingsApi.updateBuyItWith(mainId, a.related_product_id, b.sort_order);
    await settingsApi.updateBuyItWith(mainId, b.related_product_id, a.sort_order);
    loadRelated(mainId);
  };

  const removeRow = async (related_id: string) => {
    await settingsApi.removeBuyItWith(mainId!, related_id);
    loadRelated(mainId!);
  };

  const productById = (id: string) => products.find(p => p.id === id);


  return (
    <div className="space-y-6">
      <h1 className="font-display text-3xl font-bold">{t('admin.buyItWith.title')}</h1>

      <div className="surface-card rounded-lg p-4 max-w-md space-y-2">
        <label className="text-xs uppercase tracking-wide text-muted-foreground font-semibold block">{t('admin.buyItWith.selectMain')}</label>
        {loading ? (
          <div className="flex items-center gap-2 h-12 px-4 rounded-lg border border-border bg-background/60 text-sm text-muted-foreground">
            <Loader2 className="w-4 h-4 animate-spin" /> {t('admin.common.loading')}
          </div>
        ) : (
          <ProductPicker products={products} value={mainId} onChange={setMainId} lang={lang} />
        )}
      </div>

      {mainId && (
        <div className="grid md:grid-cols-2 gap-6">
          <div className="surface-card rounded-lg p-4">
            <h2 className="font-display text-lg mb-3 flex items-center justify-between">
              <span>{t('admin.buyItWith.attached')} ({related.length})</span>
              {loadingRelated && <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />}
            </h2>
            <div className="space-y-2">
              {loadingRelated && related.length === 0 && <ListSkeleton rows={3} />}
              {related.map((r, idx) => {
                const p = productById(r.related_product_id);
                return (
                  <div key={r.id} className="flex items-center gap-2 p-2 border border-border rounded">
                    {p?.main_image_url && <img src={imgUrl(p.main_image_url)} className="w-10 h-10 rounded object-cover" />}
                    <span className="flex-1 text-sm">{p ? localized(p, 'name', lang) : '—'}</span>
                    <Button size="icon" variant="ghost" onClick={() => move(idx, -1)} disabled={idx === 0}><ArrowUp className="w-4 h-4" /></Button>
                    <Button size="icon" variant="ghost" onClick={() => move(idx, 1)} disabled={idx === related.length - 1}><ArrowDown className="w-4 h-4" /></Button>
                    <Button size="icon" variant="ghost" onClick={() => removeRow(r.related_product_id)}><Trash2 className="w-4 h-4 text-destructive" /></Button>
                  </div>
                );
              })}
              {!loadingRelated && related.length === 0 && <p className="text-sm text-muted-foreground">{t('admin.buyItWith.empty')}</p>}
            </div>
          </div>

          <div className="surface-card rounded-lg p-4">
            <h2 className="font-display text-lg mb-3">{t('admin.curated.allProducts')}</h2>
            <div className="space-y-1 max-h-[60vh] overflow-y-auto">
              {loading && <ListSkeleton rows={5} />}
              {products.filter(p => p.id !== mainId).map(p => (
                <label key={p.id}
                  className={`flex items-center gap-2 p-2 rounded cursor-pointer transition-colors ${isRelated(p.id) ? 'bg-primary/10' : 'hover:bg-muted/50'}`}>
                  <Checkbox checked={isRelated(p.id)} onCheckedChange={() => toggle(p.id)} />
                  {p.main_image_url && <img src={imgUrl(p.main_image_url)} className="w-8 h-8 rounded object-cover" />}
                  <span className="text-sm flex-1">{localized(p, 'name', lang)}</span>
                </label>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
